import { THEME_TAGS } from '@/lib/themeTags';

// ── Garde-fou des balises de thème ───────────────────────────────────
// 1. Détecte les balises {{...}} Zifek inconnues ou mal orthographiées.
// 2. Détecte la syntaxe d'un AUTRE moteur de templates (style Liquid/Shopify) :
//      • balises pointées   : {{merchant.name}} {{cart.item_count}} {{product.price}}
//      • marqueurs section  : {{#products}} {{/products}} {{^foo}} {{else}}
//      • contrôle Liquid    : {% if … %} {% for … %} {% endfor %}
//    Ces balises ne sont pas comprises par le moteur Zifek et s'affichent
//    vides (ou laissent du bruit visuel) sur le site public.

const VALID_NAMES = THEME_TAGS.map((t) => t.name);
const VALID_SET = new Set(VALID_NAMES);

export interface InvalidThemeTag {
  /** Nom saisi, sans accolades */
  name: string;
  /** Nombre d'occurrences dans le contenu */
  count: number;
  /** Balise valide la plus proche, si trouvée */
  suggestion: string | null;
}

export type ForeignSyntaxKind = 'dotted' | 'section-marker' | 'liquid-control';

export interface ForeignSyntaxItem {
  kind: ForeignSyntaxKind;
  /** Nom repéré (ex: "merchant.name", "#products", "if") */
  name: string;
  /** Extrait brut affiché (ex: "{{merchant.name}}") */
  sample: string;
  /** Nombre d'occurrences */
  count: number;
  /** Balise Zifek équivalente suggérée, si connue */
  suggestion: string | null;
}

function levenshtein(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  const row = Array.from({ length: n + 1 }, (_, j) => j);
  for (let i = 1; i <= m; i += 1) {
    let prev = row[0];
    row[0] = i;
    for (let j = 1; j <= n; j += 1) {
      const tmp = row[j];
      row[j] = Math.min(row[j] + 1, row[j - 1] + 1, prev + (a[i - 1] === b[j - 1] ? 0 : 1));
      prev = tmp;
    }
  }
  return row[n];
}

/** Balise valide la plus proche (tolérance basée sur la longueur du nom). */
export function closestThemeTag(name: string): string | null {
  let best: string | null = null;
  let bestScore = Infinity;
  for (const candidate of VALID_NAMES) {
    const d = levenshtein(name.toLowerCase(), candidate.toLowerCase());
    if (d < bestScore) {
      bestScore = d;
      best = candidate;
    }
  }
  if (best === null) return null;
  const threshold = Math.max(2, Math.ceil(name.length / 3));
  return bestScore <= threshold ? best : null;
}

/**
 * Analyse un contenu de page et renvoie toutes les balises Zifek invalides
 * (balises simples inconnues / mal orthographiées), agrégées par nom.
 */
export function findInvalidThemeTags(content: string): InvalidThemeTag[] {
  if (!content) return [];
  const re = /\{\{\s*([a-zA-Z_][\w-]*)/g;
  const map = new Map<string, InvalidThemeTag>();
  let match: RegExpExecArray | null;
  while ((match = re.exec(content)) !== null) {
    const name = match[1];
    // Les noms pointés (merchant.name…) sont traités par findForeignThemeSyntax.
    if (name.includes('.') || VALID_SET.has(name)) continue;
    const entry = map.get(name) || { name, count: 0, suggestion: closestThemeTag(name) };
    entry.count += 1;
    map.set(name, entry);
  }
  return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
}

// ── Correspondances ponctuelles des balises pointées vers les balises Zifek.
const DOTTED_SUGGESTIONS: Record<string, string> = {
  'merchant.name': 'site_name',
  'shop.name': 'site_name',
  'store.name': 'site_name',
  'merchant.store_name': 'site_name',
  'merchant.description': 'site_description',
  'shop.description': 'site_description',
  'merchant.about': 'site_about',
  'merchant.about_title': 'site_about',
  'merchant.phone': 'site_phone',
  'merchant.telephone': 'site_phone',
  'merchant.email': 'site_email',
  'merchant.address': 'site_address',
  'merchant.logo': 'site_logo',
  'shop.logo': 'site_logo',
  'merchant.website': 'site_url',
  'site.copyright': 'year',
  'copyright': 'year',
  'cart.item_count': 'products',
  'routes.products': 'products',
  'featured_product.name': 'featured_products',
  'featured_product.price': 'featured_products',
  'product.name': 'products',
  'product.price': 'products',
  'products_page.title': 'products',
  'homepage.hero.title': 'hero_banners',
  'homepage.hero.subtitle': 'hero_banners',
  'homepage.hero.description': 'hero_banners',
};

function suggestForDotted(name: string): string | null {
  const lower = name.toLowerCase();
  if (DOTTED_SUGGESTIONS[lower]) return DOTTED_SUGGESTIONS[lower];
  // Essaye la correspondance par préfixe de premier segment.
  const head = lower.split('.')[0];
  switch (head) {
    case 'merchant':
    case 'shop':
    case 'store':
      return 'site_name';
    case 'product':
    case 'products':
    case 'cart':
      return 'products';
    case 'post':
    case 'blog':
      return 'blog_posts';
    case 'team':
    case 'member':
      return 'team';
    case 'routes':
      return null;
    default:
      return null;
  }
}

/**
 * Détecte la syntaxe d'un autre moteur de templates (Liquid/Shopify).
 * Renvoie une liste agrégée par extrait.
 */
export function findForeignThemeSyntax(content: string): ForeignSyntaxItem[] {
  if (!content) return [];
  const map = new Map<string, ForeignSyntaxItem>();
  const bump = (item: Omit<ForeignSyntaxItem, 'count'>) => {
    const existing = map.get(item.sample);
    if (existing) existing.count += 1;
    else map.set(item.sample, { ...item, count: 1 });
  };

  // 1. Contrôle Liquid : {% ... %}
  const controlRe = /\{%[\s\S]*?%\}/g;
  let m: RegExpExecArray | null;
  while ((m = controlRe.exec(content)) !== null) {
    bump({ kind: 'liquid-control', name: 'liquid', sample: m[0].trim().slice(0, 60), suggestion: null });
  }

  // 2. Balises {{ ... }} : marqueurs de section, pointées, else
  const tagRe = /\{\{\s*([#/^]?)\s*([\w.-]+)[^}]*\}\}/g;
  while ((m = tagRe.exec(content)) !== null) {
    const marker = m[1];
    const name = m[2];
    const raw = m[0].trim();
    if (marker === '#' || marker === '/' || marker === '^') {
      bump({ kind: 'section-marker', name: `${marker}${name}`, sample: raw, suggestion: null });
    } else if (name.toLowerCase() === 'else') {
      bump({ kind: 'section-marker', name: 'else', sample: raw, suggestion: null });
    } else if (name.includes('.')) {
      bump({ kind: 'dotted', name, sample: raw, suggestion: suggestForDotted(name) });
    }
    // Les balises simples sont gérées par findInvalidThemeTags.
  }

  return Array.from(map.values()).sort((a, b) => a.sample.localeCompare(b.sample));
}

/** Vrai si le contenu contient au moins une balise non-Zifek. */
export function hasNonZifekSyntax(content: string): boolean {
  return findForeignThemeSyntax(content).length > 0 || findInvalidThemeTags(content).length > 0;
}